import {
  ProductModel,
  ProductModelJSON,
  ProductDisplayIDModel,
} from '~/models'

export type CartModelJSON = {
  items: { [key: string]: CartItemModelJSON }
}

type CartArgs = {
  items: CartItemModel[]
}

export class CartModel {
  public readonly items: { [key: string]: CartItemModel }

  constructor(args: CartArgs) {
    this.items = {}
    for (const item of args.items) {
      this.items[item.product.displayID.toString()] = item
    }
  }

  public add(item: CartItemModel) {
    this.items[item.product.displayID.toString()] = item
  }

  public delete(displayID: ProductDisplayIDModel) {
    delete this.items[displayID.toString()]
  }

  public toEntries(): [string, CartItemModel][] {
    return Object.entries(this.items)
  }

  public toValues(): CartItemModel[] {
    return Object.values(this.items)
  }

  public toJSON(): CartModelJSON {
    const json: CartModelJSON = { items: {} }
    for (const [key, item] of this.toEntries()) {
      json.items[key] = item.toJSON()
    }
    return json
  }

  public static fromJSON(json: CartModelJSON): CartModel {
    return new CartModel({
      items: Object.values(json.items).map(item=>CartItemModel.fromJSON(item)),
    })
  }
}

export type CartItemModelJSON = {
  product: ProductModelJSON
  quantity: number
}

type CartItemArgs = {
  product: ProductModel
  quantity: number
}

export class CartItemModel {
  public readonly product: ProductModel
  public readonly quantity: number

  constructor(args: CartItemArgs) {
    if (args.product.stock <= 0) {
      throw new Error(`invalid stock: ${args.product.stock}`)
    }
    if (args.quantity > args.product.stock) {
      throw new Error(
        `quantity is greater than stock: quantity(${args.quantity}) stock(${args.product.stock})`
      )
    }
    this.product = args.product
    this.quantity = args.quantity
  }

  public toJSON(): CartItemModelJSON {
    return {
      product: this.product.toJSON(),
      quantity: this.quantity
    }
  }

  public static fromJSON(json: CartItemModelJSON): CartItemModel {
    return new CartItemModel({
      product: ProductModel.fromJSON(json.product),
      quantity: json.quantity
    })
  }
}
